/** https://github.com/nuxt/ui/blob/v3/src/runtime/composables/useComponentIcons.ts */

import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

export interface UseIconsProps {
  /** Display an icon based on the `leading` and `trailing` props. */
  icon?: string;
  /** When `true`, the icon will be displayed on the left side. */
  leading?: boolean;
  /** Display an icon on the left side. */
  leadingIcon?: string;
  /** When `true`, the icon will be displayed on the right side. */
  trailing?: boolean;
  /** Display an icon on the right side. */
  trailingIcon?: string;
  /** When `true`, the loading icon will be displayed. */
  loading?: boolean;
  /**
   * The icon when the `loading` prop is `true`.
   * @defaultValue 'reload'
   */
  loadingIcon?: string;
}
/**
 * A composable function to manage icon placement and selection logic for UI components.
 *
 * This function determines whether an icon should be placed in a leading or trailing position
 * based on the provided properties and retrieves the appropriate icon name for each position.
 *
 * @param componentProps - A reactive reference or getter function that contains icon-related properties.
 *
 * @returns An object containing computed properties:
 *  - `isLeading`: Indicates if the icon should be placed in the leading position.
 *  - `isTrailing`: Indicates if the icon should be placed in the trailing position.
 *  - `leadingIconName`: The computed icon name for the leading position.
 *  - `trailingIconName`: The computed icon name for the trailing position.
 */
export function useIcons(componentProps: MaybeRefOrGetter<UseIconsProps>) {
  const props = computed(() => toValue(componentProps));

  /**
   * Determines if the icon should be displayed in the leading position.
   */
  const isLeading = computed(
    () =>
      (props.value.icon && props.value.leading) || // Leading explicitly set
      (props.value.icon && !props.value.trailing) || // Icon present but trailing is not set
      (props.value.loading && !props.value.trailing) || // Loading state without trailing set
      !!props.value.leadingIcon // Explicit leading icon provided
  );

  /**
   * Determines if the icon should be displayed in the trailing position.
   */
  const isTrailing = computed(
    () =>
      (props.value.icon && props.value.trailing) || // Trailing explicitly set
      (props.value.loading && props.value.trailing) || // Loading state with trailing set
      !!props.value.trailingIcon // Explicit trailing icon provided
  );

  /**
   * Computes the name of the icon to be displayed in the leading position.
   * If the component is in a loading state, it prioritizes the loading icon; otherwise,
   * it falls back to the leading icon or a general icon.
   */
  const leadingIconName = computed(() => {
    if (props.value.loading) {
      return props.value.loadingIcon || 'reload';
    }

    return props.value.leadingIcon || props.value.icon;
  });

  /**
   * Computes the name of the icon to be displayed in the trailing position.
   * If the component is in a loading state and not already leading, it prioritizes
   * the loading icon; otherwise, it falls back to the trailing icon or a general icon.
   */
  const trailingIconName = computed(() => {
    if (props.value.loading && !isLeading.value) {
      return props.value.loadingIcon || 'reload';
    }

    return props.value.trailingIcon || props.value.icon;
  });

  // Return computed properties to be used in the component
  return {
    isLeading,
    isTrailing,
    leadingIconName,
    trailingIconName
  };
}
